import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Target, Heart, ArrowRight, CheckCircle } from 'lucide-react';
import { useSiteSettings } from '../contexts/SiteSettingsContext';

const About = () => {
  const { settings } = useSiteSettings();
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            À propos d'AKM Laser
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Plus qu'un vendeur de machines, un partenaire de confiance<br />
            pour votre réussite dans le monde du laser.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#5C3A21' }}>
                Notre histoire
              </h2>
              <div className="space-y-6 text-gray-700 leading-relaxed text-lg">
                <p>
                  <span className="font-bold" style={{ color: '#5C3A21' }}>2015 :</span> Début de notre aventure dans le laser. Nous avons démarré comme utilisateurs, confrontés aux mêmes défis que vous : choisir la bonne machine, apprendre à l'utiliser, optimiser les réglages.
                </p>
                <p>
                  <span className="font-bold" style={{ color: '#5C3A21' }}>2017 :</span> Face au manque de conseil et d'accompagnement sur le marché, nous avons commencé à vendre des machines laser. Notre objectif : offrir ce qui nous avait tant manqué à nos débuts.
                </p>
                <p>
                  Aujourd'hui, nous sommes fiers d'accompagner artisans, industriels et créateurs dans leurs projets laser. Notre approche reste la même : conseil honnête, machines testées, formation complète et SAV réactif.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src={settings.aboutImage1}
                alt="Notre atelier"
                className="w-full rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24" style={{ backgroundColor: '#F5EBDD' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#5C3A21' }}>
              Nos valeurs
            </h2>
            <p className="text-xl text-gray-700">
              Ce qui guide chacune de nos actions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Heart,
                title: 'Approche humaine',
                description: 'Écoute, disponibilité et conseil personnalisé. Vous n\'êtes pas un numéro, vous êtes un partenaire.'
              },
              {
                icon: CheckCircle,
                title: 'Honnêteté',
                description: 'Nous vous conseillons la machine dont vous avez VRAIMENT besoin, pas la plus chère.'
              },
              {
                icon: Award,
                title: 'Expertise',
                description: 'Expérience terrain depuis 2015. Nous connaissons les machines car nous les utilisons.'
              },
              {
                icon: Target,
                title: 'Engagement',
                description: 'Nous nous engageons sur votre réussite. Formation, SAV, conseils : nous sommes là.'
              }
            ].map((value, idx) => (
              <div
                key={idx}
                className="p-8 rounded-2xl bg-white border border-amber-100 hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 group"
              >
                <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#F5EBDD' }}>
                  <value.icon className="w-8 h-8" style={{ color: '#5C3A21' }} />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#5C3A21' }}>
                  {value.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why We're Different */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <img
                src={settings.aboutImage2}
                alt="Équipe AKM Laser"
                className="w-full rounded-2xl shadow-2xl"
              />
            </div>

            <div className="order-1 lg:order-2">
              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#5C3A21' }}>
                Ce qui nous différencie
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Expertise terrain',
                    text: 'Nous utilisons nous-mêmes les machines que nous vendons. Nos conseils sont basés sur l\'expérience réelle, pas sur une fiche technique.'
                  },
                  {
                    title: 'Sélection rigoureuse',
                    text: 'Nous testons toutes nos machines avant de les proposer. Cahier des charges strict sur la fiabilité, la précision et la durabilité.'
                  },
                  {
                    title: 'Formation complète',
                    text: 'Formation incluse dans chaque vente. Pas de vidéos YouTube à regarder seul : une vraie formation en présentiel avec nos experts.'
                  },
                  {
                    title: 'SAV qui répond',
                    text: 'Un numéro de téléphone qui décroche. Un email qui obtient une réponse rapide. Assistance à distance et intervention sur site si nécessaire.'
                  },
                  {
                    title: 'Relation long terme',
                    text: 'Nous ne disparaissons pas après la vente. Vous avez besoin de conseils 6 mois après ? On est là. Un réglage à optimiser ? On vous aide.'
                  }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-4">
                    <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: '#5C3A21' }} />
                    <div>
                      <h3 className="font-bold text-lg mb-1" style={{ color: '#5C3A21' }}>{item.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {[
              { number: '2015', label: 'Année de création' },
              { number: '200+', label: 'Machines vendues' },
              { number: '24/7', label: 'Support disponible' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-5xl md:text-6xl font-bold mb-2 bg-gradient-to-r from-amber-200 to-amber-400 bg-clip-text text-transparent">
                  {stat.number}
                </div>
                <div className="text-gray-300 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#5C3A21' }}>
            Rejoignez nos clients satisfaits
          </h2>
          <p className="text-xl text-gray-700 mb-10 leading-relaxed">
            Artisans, industriels, créateurs : ils ont tous réussi avec AKM Laser.<br />
            C'est maintenant votre tour !
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 rounded-xl text-white font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 text-lg"
            style={{ backgroundColor: '#5C3A21' }}
          >
            Démarrer mon projet
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
